import React, { useRef, useState, useEffect } from "react"
import Textarea from "react-textarea-autosize"
import cx from "classnames"
import DonutChart from "./charts/DonutChart"
import { isZeroValue } from "../utils/utils"
import numeral from "numeral"

const AutoSaveField = ({
    defaultValue,
    value,
    onSave,
    style,
    onBlur,
    to = 500,
    minValue,
    maxValue,
    onChange,
    required,
    className,
    resetOnError,
    autoSelect,
    placeHolder,
    maxRows,
    autoFocus,
    saveOnExit,
    onKeyDown,
    onKeyPress,
    type = "text",
    id,
    maxLength,
}) => {
    const field = useRef()
    const sto = useRef()
    const lastSavedValue = useRef()
    // Holds { raw } while the user has typed something that has not been saved yet.
    // Null means "nothing outstanding" - which is what makes saveOnExit safe.
    const pendingEdit = useRef(null)
    const seeded = useRef(false)

    const initialValue = isZeroValue(defaultValue)
        ? "0"
        : defaultValue !== null && defaultValue !== undefined
          ? defaultValue
          : (value ?? "")

    const [valueState, setValueState] = useState(initialValue)
    const [error, setError] = useState(false)
    const [showCounter, setShowCounter] = useState(false)

    const getValue = (v) => {
        // Treat 'money' exactly like 'number' for processing raw values
        if (type === "number" || type === "money") {
            if (v === "" || v === null || v === undefined) {
                return null
            }
            // Remove commas before parsing to a real number
            const n = Number(v.toString().replace(/,/g, ""))
            return isNaN(n) ? null : n
        }
        return v?.length ? v : null
    }

    // null / undefined / "" all mean "no value". 0 is a real value and is never blank.
    const isBlank = (v) => v === null || v === undefined || v === ""

    // Change detection for the save gate. Deliberately avoids ==/!= so that
    // 0 vs null, 0 vs "" and null vs "" are never confused with each other.
    const isUnchanged = (a, b) => {
        const aBlank = isBlank(a)
        const bBlank = isBlank(b)
        if (aBlank || bBlank) return aBlank && bBlank
        if (type === "number" || type === "money") return Number(a) === Number(b)
        return String(a) === String(b)
    }

    // Seed the baseline once, so an untouched field can never fire a save.
    if (!seeded.current) {
        seeded.current = true
        lastSavedValue.current = getValue(initialValue)
    }

    // Keep valueState in sync with external value when needed
    useEffect(() => {
        if (value !== undefined) {
            setValueState(value)
            // The new value came from the outside, so it is already saved and it
            // supersedes anything the user had typed but not committed.
            lastSavedValue.current = getValue(value)
            pendingEdit.current = null
        }
    }, [value])

    // Validates and normalises a raw input value. `silent` skips every state
    // update so this can safely run while the component is unmounting.
    const resolveSaveValue = (v, fromBlurEvent, silent) => {
        let val = getValue(v)
        let forceSave = false

        if (type === "number") {
            // Only clamp a real number - a cleared field stays cleared instead
            // of being snapped up to minValue.
            if (val !== null) {
                if (maxValue !== undefined && val > maxValue) {
                    if (!fromBlurEvent) {
                        if (!silent) setError(true)
                        return null
                    }
                    val = maxValue
                    forceSave = true
                }

                if (minValue !== undefined && val < minValue) {
                    if (!fromBlurEvent) {
                        if (!silent) setError(true)
                        return null
                    }
                    val = minValue
                    forceSave = true
                }
            }

            if (val === null) {
                if (!silent) setValueState("")
            } else if (val < 0) {
                if (!silent) setValueState("0")
                return null
            } else if (!silent) {
                setValueState(val.toString())
            }
        } else if (maxLength && val?.length > maxLength) {
            val = val.slice(0, maxLength)
            if (!silent) setValueState(val)
        }

        // Blank means blank - a required number field still accepts 0.
        if (required && isBlank(val)) {
            if (!silent) setError(true)
            return null
        }
        if (!silent) setError(false)

        return { val, forceSave }
    }

    // The single place onSave is ever called from.
    const commitSave = (val, forceSave) => {
        pendingEdit.current = null
        if (!forceSave && isUnchanged(lastSavedValue.current, val)) return
        lastSavedValue.current = val
        onSave(val)
    }

    const onReadyToSave = (v, fromBlurEvent) => {
        const resolved = resolveSaveValue(v, fromBlurEvent, false)
        if (!resolved) {
            // Rejected by validation - drop the edit so exiting cannot save it.
            pendingEdit.current = null
            return
        }

        if (onChange) onChange(v)
        commitSave(resolved.val, resolved.forceSave)
    }

    // Runs while unmounting: no refs, no DOM reads, no state updates.
    const flushPendingEdit = () => {
        const pending = pendingEdit.current
        if (!pending) return
        pendingEdit.current = null

        const resolved = resolveSaveValue(pending.raw, true, true)
        if (!resolved) return

        if (onChange) onChange(pending.raw)
        commitSave(resolved.val, resolved.forceSave)
    }

    const latest = useRef({})
    latest.current = { autoSelect, saveOnExit, flushPendingEdit }

    // Focus/select only. This re-runs whenever autoFocus flips, so it must never
    // touch the save path.
    useEffect(() => {
        if (!autoFocus) return

        const focusTimeout = setTimeout(() => {
            if (!field.current) return

            if (!latest.current.autoSelect) {
                const len = field.current.value?.length
                field.current.setSelectionRange(len, len)
            }

            field.current.focus()
            if (latest.current.autoSelect) {
                field.current.select()
            }
        }, 500)

        return () => clearTimeout(focusTimeout)
    }, [autoFocus])

    // Empty deps: this cleanup runs on real unmount only, never on re-render.
    // Saves only if the debounce timer was still holding an unsaved edit.
    useEffect(() => {
        return () => {
            clearTimeout(sto.current)
            if (latest.current.saveOnExit) latest.current.flushPendingEdit()
        }
    }, [])

    // 1. Keep your onChangeHandler completely standard for numbers/money
    const onChangeHandler = (e) => {
        let v = e.target.value

        if (type === "number" || type === "money") {
            // Strip everything except digits, decimals, and minus signs
            v = v.replace(/[^\d.-]/g, "")
        }

        setValueState(v)

        // Remember the edit so an unmount before the timer fires can still save it.
        pendingEdit.current = { raw: v }

        clearTimeout(sto.current)
        if (to !== Infinity) {
            sto.current = setTimeout(() => {
                onReadyToSave(v) // This sends the clean number automatically
            }, to)
        }
    }

    const onBlurMe = (e) => {
        clearTimeout(sto.current)
        let v = valueState

        if ((type === "number" || type === "money") && v !== "") {
            const numericValue = getValue(v)
            if (numericValue !== null) {
                if (type === "money") {
                    // Forces 2 decimal places and commas: 35 -> 35.00
                    setValueState(numeral(numericValue).format("0,0.00"))
                } else {
                    setValueState(numeral(numericValue).format("0,0.[00]"))
                }
            }
        }

        setShowCounter(false)
        onReadyToSave(v, true)

        if (onBlur) onBlur()
    }
    const getDisplayValue = () => {
        // If the field is currently focused, show the raw unformatted string (no commas)
        if (document.activeElement === field.current) {
            return valueState ?? ""
        }

        if (type === "money") {
            // If it's null, undefined, an empty string, or evaluated to 0, show empty
            if (valueState === "" || valueState === null || valueState === undefined || Number(valueState) === 0) {
                return ""
            }
            // Otherwise, format it normally
            return numeral(valueState).format("0,0.00")
        }

        return valueState ?? ""
    }

    return (
        <>
            {type === "text" || type === "number" || type === "money" ? (
                <input
                    id={id}
                    ref={field}
                    className={cx(className, error && "squigly-red", {
                        "dna-right-text": type === "number" || type === "money", // Right align money too
                    })}
                    value={getDisplayValue()}
                    placeholder={placeHolder}
                    type="text"
                    inputMode={type === "number" || type === "money" ? "decimal" : "text"}
                    onChange={onChangeHandler}
                    onBlur={onBlurMe}
                    onKeyDown={onKeyDown}
                    style={style}
                    onFocus={(e) => {
                        if (type === "money" && valueState) {
                            // Strip commas so '1,250.00' becomes '1250.00' for clean editing
                            setValueState(valueState.toString().replace(/,/g, ""))
                        }
                        if (autoSelect) e.target.select()
                    }}
                />
            ) : (
                <Textarea
                    id={id}
                    ref={field}
                    maxRows={maxRows}
                    className={cx(className, error && "squigly-red")}
                    placeholder={placeHolder}
                    value={valueState ?? ""}
                    onChange={onChangeHandler}
                    onKeyDown={onKeyDown}
                    onBlur={onBlurMe}
                    onKeyPress={onKeyPress}
                    style={style}
                    onFocus={maxLength ? () => setShowCounter(true) : undefined}
                />
            )}
            {required && error && (
                <div className="dna-label dna-issue-text" style={{ marginTop: 0 }}>
                    <b>Required</b>
                </div>
            )}

            {showCounter && maxLength && <MaxLength test={valueState} max={maxLength} />}
        </>
    )
}

export const MaxLength = ({ test, max }) => {
    if (!test?.length) return null
    const count = test.length
    const p = count > max ? 100 : (count / max) * 100

    return (
        <div className="x-mx-length">
            {max - count}{" "}
            <DonutChart
                customLabel={" "}
                className={p === 100 ? "red" : p > 80 ? "gold" : "blue"}
                percentage={p}
                thickness={3}
                size={40}
            />
        </div>
    )
}

export default AutoSaveField
